import { createContext, useContext, useState, useEffect } from "react";
import { createTheme } from "@mui/material/styles";

export const ThemeContext = createContext();

// ── Token definitions — single source of truth for every color ───────────────
// Add light-mode counterparts here when the light theme is implemented.
const DARK_TOKENS = {
  bgDefault:        "#000000",
  bgPaper:          "#0d0d0d",
  bgHighlight:      "#141414",
  bgGray:           "#111111",
  green:            "#62f481",
  greenSecondary:   "#05df72",
  greenEmerald:     "#44aa5a",
  textPrimary:      "#f3f4f6",
  textSecondary:    "#99a1af",
  textMuted:        "#6a7282",
  textGreen:        "#62f481",
  tagIndex:         "#e6e666",
  tagCrypto:        "#7e90ea",
  tagStock:         "#62f481",
  tagCommunity:     "#177bcc",
  border:           "rgba(255,255,255,0.07)",
  borderSubtle:     "rgba(255,255,255,0.15)",
  borderGreen:      "rgba(98,244,129,0.3)",
  borderDivider:    "rgba(98,244,129,0.2)",
  error:            "#ff6467",
  darkGreen:        "#31bb4f",
  cancel:           "#ff5e39",
  cancelHighlight:  "#c33906",
};

// Inject tokens as CSS custom properties on :root so CSS files can use them.
// This function runs on mount and every time the theme changes.
function injectCssVars(tokens) {
  const root = document.documentElement;
  root.style.setProperty("--sc-bg-default",       tokens.bgDefault);
  root.style.setProperty("--sc-bg-paper",          tokens.bgPaper);
  root.style.setProperty("--sc-bg-highlight",      tokens.bgHighlight);
  root.style.setProperty("--sc-bg-gray",           tokens.bgGray);
  root.style.setProperty("--sc-green",             tokens.green);
  root.style.setProperty("--sc-green-secondary",   tokens.greenSecondary);
  root.style.setProperty("--sc-green-emerald",     tokens.greenEmerald);
  root.style.setProperty("--sc-text-primary",      tokens.textPrimary);
  root.style.setProperty("--sc-text-secondary",    tokens.textSecondary);
  root.style.setProperty("--sc-text-muted",        tokens.textMuted);
  root.style.setProperty("--sc-border",            tokens.border);
  root.style.setProperty("--sc-border-subtle",     tokens.borderSubtle);
  root.style.setProperty("--sc-border-green",      tokens.borderGreen);
  root.style.setProperty("--sc-border-divider",    tokens.borderDivider);
  root.style.setProperty("--sc-error",             tokens.error);
}

function buildTheme(tokens) {
  return createTheme({
    palette: {
      mode: "dark",
      primary: {
        main:  tokens.green,
        light: tokens.greenSecondary,
        dark:  tokens.greenEmerald,
      },
      secondary: {
        main: tokens.greenSecondary,
      },
      text: {
        primary:   tokens.textPrimary,
        secondary: tokens.textSecondary,
        muted:     tokens.textMuted,
        green:     tokens.textGreen,
        disabled:  tokens.textMuted,
        dark:      tokens.bgPaper,
      },
      tags: {
        index:     tokens.tagIndex,
        crypto:    tokens.tagCrypto,
        stock:     tokens.tagStock,
        community: tokens.tagCommunity,
      },
      background: {
        default:         tokens.bgDefault,
        paper:           tokens.bgPaper,
        highlight:       tokens.bgHighlight,
        card:            tokens.bgPaper,
        gray:            tokens.bgGray,
        unread:          tokens.bgDefault + "bb",
        read:            tokens.bgDefault + "00",
        darkGreen:       tokens.darkGreen,
        cancel:          tokens.cancel,
        cancelHighlight: tokens.cancelHighlight,
      },
      divider: tokens.border,
      border: {
        main:    tokens.border,
        subtle:  tokens.borderSubtle,
        green:   tokens.borderGreen,
        divider: tokens.borderDivider,
      },
      success: { main: tokens.greenEmerald },
      error:   { main: tokens.error },
    },
  });
}

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState("dark");

  // Currently only dark mode is fully designed; light tokens can be added later.
  const tokens = DARK_TOKENS;
  const themeConfig = buildTheme(tokens);

  // Keep CSS variables in sync with the active token set
  useEffect(() => {
    injectCssVars(tokens);
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ThemeContext.Provider
      value={{
        theme: themeConfig,
        mode,
        tokens,
        toggleTheme: () => setMode((m) => (m === "dark" ? "light" : "dark")),
      }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
